import { StringContext } from "../../core/StringContext";
import { StringData } from "../../core/StringData";
import { StringModule } from "../../core/StringModule";
import { StringObject } from "../../objects/StringObject";
import { isCoarsePointer } from "../../utils/isCoarsePointer";
import { styleTxn } from "../../utils/style-txn";

type CursorTargetState = {
  prevX: number;
  prevY: number;
};

type CursorPrevSnapshot = {
  x: number;
  y: number;
  stepX: number;
  stepY: number;
};

type CursorPortal = {
  id: string;
  element: HTMLElement;
  content: HTMLElement | null;
  prev: CursorPrevSnapshot;
  hoverCount: number;
  showTimer: ReturnType<typeof setTimeout> | null;
};

const targetStates = new WeakMap<StringObject, CursorTargetState>();
const CURSOR_EPSILON = 0.005;
const DEFAULT_CURSOR_ID = "default";
const CURSOR_PORTAL_SELECTOR = "[string-cursor],[data-string-cursor]";
const CURSOR_PORTAL_CONTENT_SELECTOR = "[string-cursor-content],[data-string-cursor-content]";

function getTargetState(object: StringObject): CursorTargetState {
  let state = targetStates.get(object);
  if (!state) {
    state = { prevX: Number.NaN, prevY: Number.NaN };
    targetStates.set(object, state);
  }
  return state;
}

/**
 * StringCursor Module
 *
 * Handles cursor tracking and hover states for StringTune objects.
 *
 * Safari Navigation Fix:
 * Safari has an issue where mouseleave events are not fired when navigation occurs
 * (especially with NuxtLink/router navigation). This module includes several
 * workarounds to ensure proper cleanup:
 *
 * 1. MutationObserver - detects when elements are removed from DOM
 * 2. beforeunload/pagehide - captures traditional navigation
 * 3. visibilitychange - captures modern SPA navigation
 * 4. DOM existence checks - prevents operations on removed elements
 */
export class StringCursor extends StringModule {
  private cursorPrev: CursorPrevSnapshot = {
    x: Number.NaN,
    y: Number.NaN,
    stepX: Number.NaN,
    stepY: Number.NaN,
  };
  private cursorPortals: Map<string, CursorPortal[]> = new Map();
  private hoveredObjects: Set<StringObject> = new Set();
  private globalListenersBound = false;
  private boundBeforeUnload = () => this.cleanupHoverTargets();
  private boundPageHide = () => this.cleanupHoverTargets();
  private boundVisibilityChange = () => {
    if (document.hidden) {
      this.cleanupHoverTargets();
    }
  };
  protected enabled = true;

  constructor(context: StringContext) {
    super(context);
    this.htmlKey = "cursor";

    this.permissions.mobile.rebuild.height = false;
    this.permissions.mobile.rebuild.width = false;
    this.permissions.mobile.rebuild.scrollHeight = false;

    this.attributesToMap = [
      ...this.attributesToMap,
      {
        key: "target-disable",
        type: "boolean",
        fallback: this.settings["target-disable"],
      },
      {
        key: "target-style-disable",
        type: "boolean",
        fallback: this.settings["target-style-disable"],
      },
      {
        key: "cursor-target",
        type: "string",
        fallback: this.settings["cursor-target"] ?? DEFAULT_CURSOR_ID,
      },
      {
        key: "target-class",
        type: "string",
        fallback: this.settings["target-class"],
      },
      {
        key: "cursor-class",
        type: "string",
        fallback: this.settings["cursor-class"],
      },
      {
        key: "alignment",
        type: { type: "enum", values: ["start", "center", "end"] },
        fallback: this.settings["alignment"],
      },
      {
        key: "lerp",
        type: "number",
        fallback: this.settings["lerp"],
        transform: (value: number) => {
          return this.tools.adaptiveLerp.process({
            value,
            inMin: 0.1,
            inMax: 1.0,
            outMin: 0.05,
            outMax: 0.65,
          });
        },
      },
    ];

    if (isCoarsePointer()) {
      this.enabled = false;
    }

    this.collectCursorPortals();
    if (this.enabled) {
      this.bindGlobalLifecycleListeners();
    }
  }

  initializeObject(
    globalId: number,
    object: StringObject,
    element: HTMLElement,
    attributes: Record<string, any>
  ): void {
    super.initializeObject(globalId, object, element, attributes);
    object.setProperty("mouse-x", 0);
    object.setProperty("mouse-y", 0);
    object.setProperty("mouse-pixel-x", 0);
    object.setProperty("mouse-pixel-y", 0);
    object.setProperty("is-mouse-over", false);
    object.setProperty("is-mouse-move", false);
  }

  onMutate(data: StringData): void {
    if (!this.enabled) return;
    const cursorX = this.data.cursor.targetX;
    const cursorY = this.data.cursor.targetY;

    this.objects.forEach((object) => {
      const isOver = object.getProperty<boolean>("is-mouse-over");
      const isDisabled = object.getProperty<boolean>("cursor-target-disable");
      const lerpFactor = object.getProperty<number>("lerp") ?? 0.15;

      const halfWidth =
        object.getProperty<number>("half-width") ?? object.htmlElement.offsetWidth / 2;
      const halfHeight =
        object.getProperty<number>("half-height") ?? object.htmlElement.offsetHeight / 2;
      const width = halfWidth * 2 || object.htmlElement.offsetWidth || 1;
      const height = halfHeight * 2 || object.htmlElement.offsetHeight || 1;

      if (isOver && !isDisabled) {
        const rect = object.htmlElement.getBoundingClientRect();
        const { cx, cy } = this.centers.getCenter(object);
        const elementX = cursorX - (cx - halfWidth);
        const elementY = cursorY - (cy - halfHeight);

        let px = object.getProperty<number>("mouse-pixel-x") ?? 0;
        let py = object.getProperty<number>("mouse-pixel-y") ?? 0;

        const dx = px - elementX;
        const dy = py - elementY;
        const distSquared = dx * dx + dy * dy;

        if (distSquared > 0.0001) {
          const isMoving = object.getProperty<boolean>("is-mouse-move") ?? false;
          if (!isMoving) {
            object.setProperty("is-mouse-move", true);
            object.setProperty("mouse-pixel-x", elementX);
            object.setProperty("mouse-pixel-y", elementY);
            object.setProperty("mouse-x", elementX);
            object.setProperty("mouse-y", elementY);
            px = elementX;
            py = elementY;
            this.events.emit(`cursor:start:${object.id}`, null);
          }

          const lerpedX = this.tools.lerp.process({
            from: px,
            to: elementX,
            progress: lerpFactor,
          });
          const lerpedY = this.tools.lerp.process({
            from: py,
            to: elementY,
            progress: lerpFactor,
          });

          const updatedX = px + lerpedX;
          const updatedY = py + lerpedY;
          const pixelChanged =
            Math.abs(updatedX - px) > CURSOR_EPSILON || Math.abs(updatedY - py) > CURSOR_EPSILON;

          object.setProperty("mouse-pixel-x", updatedX);
          object.setProperty("mouse-pixel-y", updatedY);

          const alignment = object.getProperty<string>("alignment") ?? "center";
          const offsetX = this.calculateOffset(alignment, updatedX, width);
          const offsetY = this.calculateOffset(alignment, updatedY, height);

          object.setProperty("mouse-x", offsetX);
          object.setProperty("mouse-y", offsetY);

          const coordsChanged = this.setMouseCoordinates(object, offsetX, offsetY);

          if (coordsChanged) {
            this.events.emit(`cursor:move:${object.id}`, {
              x: offsetX,
              y: offsetY,
            });
          }
          if (pixelChanged) {
            this.events.emit(`cursor:pixel:${object.id}`, {
              x: updatedX,
              y: updatedY,
            });
          }
        } else {
          object.setProperty("mouse-pixel-x", elementX);
          object.setProperty("mouse-pixel-y", elementY);
          if (object.getProperty<boolean>("is-mouse-move")) {
            object.setProperty("is-mouse-move", false);
            this.events.emit(`cursor:end:${object.id}`, null);
          }
          this.setMouseCoordinates(object, 0, 0);
        }
      } else {
        const mouseX = object.getProperty<number>("mouse-x") ?? 0;
        const mouseY = object.getProperty<number>("mouse-y") ?? 0;
        if (mouseX !== 0 || mouseY !== 0) {
          object.setProperty("is-mouse-move", false);

          const targetX = this.calculateOffset("center", halfWidth, width);
          const targetY = this.calculateOffset("center", halfHeight, height);

          const newMouseX =
            mouseX +
            this.tools.lerp.process({
              from: mouseX,
              to: targetX,
              progress: lerpFactor,
            });
          const newMouseY =
            mouseY +
            this.tools.lerp.process({
              from: mouseY,
              to: targetY,
              progress: lerpFactor,
            });

          object.setProperty("mouse-x", newMouseX);
          object.setProperty("mouse-y", newMouseY);

          if (Math.abs(newMouseX) < 0.001 && Math.abs(newMouseY) < 0.001) {
            object.setProperty("mouse-x", 0);
            object.setProperty("mouse-y", 0);
            object.setProperty("mouse-pixel-x", 0);
            object.setProperty("mouse-pixel-y", 0);
            this.setMouseCoordinates(object, 0, 0);
          } else {
            this.setMouseCoordinates(object, newMouseX, newMouseY);
          }
        }
      }
    });

    if (this.cursorPortals.size > 0) {
      const { stepX, stepY, smoothedX, smoothedY } = this.data.cursor;
      const prev = this.cursorPrev;
      const cursorChanged =
        !Number.isFinite(prev.x) ||
        Math.abs(smoothedX - prev.x) > CURSOR_EPSILON ||
        Math.abs(smoothedY - prev.y) > CURSOR_EPSILON ||
        Math.abs(stepX - prev.stepX) > CURSOR_EPSILON ||
        Math.abs(stepY - prev.stepY) > CURSOR_EPSILON;

      if (cursorChanged) {
        this.events.emit("cursor", {
          stepX,
          stepY,
          x: smoothedX,
          y: smoothedY,
        });

        this.cursorPrev = { x: smoothedX, y: smoothedY, stepX, stepY };
      }

      this.cursorPortals.forEach((bucket) => {
        bucket.forEach((portal) => {
          this.updatePortalPosition(portal, smoothedX, smoothedY, stepX, stepY);
        });
      });
    }
  }

  onObjectConnected(object: StringObject) {
    const element = object.htmlElement;
    this.centers.attach(object);

    object.setProperty("mouseleave", () => {
      this.onMouseLeave(object);
    });
    object.setProperty("mouseenter", () => {
      this.onMouseEnter(object);
    });

    object.setProperty("onEnterEvent", this.onEnterObject.bind(this));
    object.events.on("enter", object.getProperty<(object: StringObject) => void>("onEnterEvent"));
    object.setProperty("onLeaveEvent", this.onLeaveObject.bind(this));
    object.events.on("leave", object.getProperty<(object: StringObject) => void>("onLeaveEvent"));
  }

  getCursorClass(object: StringObject) {
    const value = object.getProperty<string>("cursor-class");
    return value != null && value.length > 0 ? value : null;
  }

  onMouseEnter(object: StringObject) {
    if (!document.contains(object.htmlElement)) {
      return;
    }

    object.setProperty("is-mouse-over", true);
    this.hoveredObjects.add(object);

    const cursorClass = this.getCursorClass(object);

    this.withPortalsForObject(object, (portal) => {
      if (cursorClass) {
        portal.element.classList.add(cursorClass);
      }
      this.incrementPortalHover(portal);
    });

    object.htmlElement.addEventListener("mouseleave", object.getProperty("mouseleave"));
  }

  onMouseLeave(object: StringObject) {
    object.setProperty("is-mouse-over", false);
    this.hoveredObjects.delete(object);

    const cursorClass = this.getCursorClass(object);

    this.withPortalsForObject(object, (portal) => {
      if (cursorClass) {
        portal.element.classList.remove(cursorClass);
      }
      this.decrementPortalHover(portal);
    });

    if (document.contains(object.htmlElement)) {
      object.htmlElement.removeEventListener("mouseleave", object.getProperty("mouseleave"));
    }
  }

  private onEnterObject(object: StringObject) {
    object.htmlElement.addEventListener("mouseenter", object.getProperty("mouseenter"));
  }

  private onLeaveObject(object: StringObject) {
    object.htmlElement.removeEventListener("mouseenter", object.getProperty("mouseenter"));
    object.htmlElement.removeEventListener("mouseleave", object.getProperty("mouseleave"));
  }

  private safariNavigationCleanup(object: StringObject): void {
    if (object.getProperty<boolean>("is-mouse-over")) {
      this.onMouseLeave(object);
    }
  }

  private onElementRemovedFromDOM(object: StringObject): void {
    if (object.getProperty<boolean>("is-mouse-over")) {
      this.onMouseLeave(object);
    }
  }

  onObjectDisconnected(object: StringObject): void {
    if (object.getProperty<boolean>("is-mouse-over")) {
      this.onMouseLeave(object);
    }
  }

  onDOMRebuild(): void {
    if (!this.enabled) return;
    this.collectCursorPortals();
  }

  onDOMMutate(added: NodeList, removed: NodeList): void {
    if (!this.enabled) return;
    if (this.shouldRefreshPortals(added) || this.shouldRefreshPortals(removed)) {
      this.collectCursorPortals();
    }
    if (removed.length > 0) {
      this.handleRemovedNodes(removed);
    }
  }

  private collectCursorPortals(): void {
    this.cursorPortals.clear();
    const nodes = document.querySelectorAll(CURSOR_PORTAL_SELECTOR);
    nodes.forEach((node) => {
      if (!(node instanceof HTMLElement)) return;
      const id = this.resolvePortalId(node);
      const content = node.matches(CURSOR_PORTAL_CONTENT_SELECTOR)
        ? node
        : (node.querySelector(CURSOR_PORTAL_CONTENT_SELECTOR) as HTMLElement | null);

      const portal: CursorPortal = {
        id,
        element: node,
        content,
        prev: { x: Number.NaN, y: Number.NaN, stepX: Number.NaN, stepY: Number.NaN },
        hoverCount: 0,
        showTimer: null,
      };

      const existing = this.cursorPortals.get(id);
      if (existing) {
        existing.push(portal);
      } else {
        this.cursorPortals.set(id, [portal]);
      }
    });
  }

  private resolvePortalId(element: HTMLElement): string {
    const attrSources = [
      element.getAttribute("data-string-cursor"),
      element.getAttribute("string-cursor"),
      element.getAttribute("data-string-cursor-id"),
      element.getAttribute("string-cursor-id"),
    ];

    for (const source of attrSources) {
      if (source && source.trim().length > 0) {
        return source.trim();
      }
    }

    return DEFAULT_CURSOR_ID;
  }

  private shouldRefreshPortals(nodes: NodeList): boolean {
    for (const node of nodes) {
      if (!(node instanceof Element)) continue;
      if (node.matches(CURSOR_PORTAL_SELECTOR)) {
        return true;
      }
      if (node.querySelector(CURSOR_PORTAL_SELECTOR)) {
        return true;
      }
    }
    return false;
  }

  private withPortalsForObject(
    object: StringObject | null,
    cb: (portal: CursorPortal) => void
  ): void {
    const portals = this.getPortalsForObject(object);
    portals.forEach((portal) => cb(portal));
  }

  private getPortalsForObject(object: StringObject | null): CursorPortal[] {
    if (this.cursorPortals.size === 0) {
      return [];
    }

    const ids = this.extractPortalIds(object);
    const selected: CursorPortal[] = [];

    ids.forEach((id) => {
      if (id === "*") {
        this.cursorPortals.forEach((bucket) => {
          bucket.forEach((portal) => selected.push(portal));
        });
        return;
      }

      const normalized = id.length > 0 ? id : DEFAULT_CURSOR_ID;
      const portals = this.cursorPortals.get(normalized);
      if (portals) {
        portals.forEach((portal) => selected.push(portal));
      }
    });

    if (selected.length === 0) {
      const fallbackBucket =
        this.cursorPortals.get(DEFAULT_CURSOR_ID) ?? this.cursorPortals.values().next().value;
      if (fallbackBucket && fallbackBucket.length > 0) {
        fallbackBucket.forEach((portal: any) => selected.push(portal));
      }
    }

    return selected;
  }

  private extractPortalIds(object: StringObject | null): string[] {
    if (!object) return [DEFAULT_CURSOR_ID];
    const raw = object.getProperty<string>("cursor-target");
    if (typeof raw !== "string" || raw.trim().length === 0) {
      return [DEFAULT_CURSOR_ID];
    }

    return raw
      .split(/[,|]/)
      .map((value) => value.trim())
      .filter(Boolean);
  }

  private incrementPortalHover(portal: CursorPortal): void {
    portal.hoverCount++;
    portal.element.classList.add("-showing");
    portal.element.classList.remove("-show");
    this.restartPortalShowTimer(portal);
  }

  private decrementPortalHover(portal: CursorPortal): void {
    portal.hoverCount = Math.max(0, portal.hoverCount - 1);
    if (portal.hoverCount === 0) {
      this.clearPortalShowTimer(portal);
      portal.element.classList.remove("-showing");
      portal.element.classList.remove("-show");
    }
  }

  private restartPortalShowTimer(portal: CursorPortal): void {
    this.clearPortalShowTimer(portal);
    portal.showTimer = setTimeout(() => {
      if (!portal.element.isConnected) {
        portal.showTimer = null;
        return;
      }
      portal.element.classList.remove("-showing");
      portal.element.classList.add("-show");
      portal.showTimer = null;
    }, 1200);
  }

  private clearPortalShowTimer(portal: CursorPortal): void {
    if (portal.showTimer) {
      clearTimeout(portal.showTimer);
      portal.showTimer = null;
    }
  }

  private updatePortalPosition(
    portal: CursorPortal,
    smoothedX: number,
    smoothedY: number,
    stepX: number,
    stepY: number
  ): void {
    if (!portal.element.isConnected) {
      return;
    }

    const prev = portal.prev;
    const changed =
      !Number.isFinite(prev.x) ||
      Math.abs(smoothedX - prev.x) > CURSOR_EPSILON ||
      Math.abs(smoothedY - prev.y) > CURSOR_EPSILON ||
      Math.abs(stepX - prev.stepX) > CURSOR_EPSILON ||
      Math.abs(stepY - prev.stepY) > CURSOR_EPSILON;

    if (!changed) {
      return;
    }

    portal.element.style.setProperty("--x", smoothedX.toString());
    portal.element.style.setProperty("--y", smoothedY.toString());
    portal.element.style.setProperty("--x-lerp", stepX.toString());
    portal.element.style.setProperty("--y-lerp", stepY.toString());

    portal.prev = { x: smoothedX, y: smoothedY, stepX, stepY };
  }

  private handleRemovedNodes(nodes: NodeList): void {
    if (this.hoveredObjects.size === 0) {
      return;
    }
    Array.from(this.hoveredObjects).forEach((object) => {
      if (!object.htmlElement.isConnected) {
        this.onElementRemovedFromDOM(object);
      }
    });
  }

  private cleanupHoverTargets(): void {
    if (this.hoveredObjects.size === 0) {
      return;
    }
    Array.from(this.hoveredObjects).forEach((object) => this.safariNavigationCleanup(object));
  }

  private bindGlobalLifecycleListeners(): void {
    if (this.globalListenersBound) {
      return;
    }
    window.addEventListener("beforeunload", this.boundBeforeUnload);
    window.addEventListener("pagehide", this.boundPageHide);
    document.addEventListener("visibilitychange", this.boundVisibilityChange);
    this.globalListenersBound = true;
  }

  private unbindGlobalLifecycleListeners(): void {
    if (!this.globalListenersBound) {
      return;
    }
    window.removeEventListener("beforeunload", this.boundBeforeUnload);
    window.removeEventListener("pagehide", this.boundPageHide);
    document.removeEventListener("visibilitychange", this.boundVisibilityChange);
    this.globalListenersBound = false;
  }

  private setMouseCoordinates(object: StringObject, x: number, y: number): boolean {
    if (object.getProperty("cursor-target-style-disable")) {
      return false;
    }

    const state = getTargetState(object);
    const roundedX = Math.abs(x) < CURSOR_EPSILON ? state.prevX : Math.round(x * 100) / 100;
    const roundedY = Math.abs(y) < CURSOR_EPSILON ? state.prevY : Math.round(y * 100) / 100;

    if (
      Number.isFinite(state.prevX) &&
      Math.abs(roundedX - state.prevX) <= CURSOR_EPSILON &&
      Number.isFinite(state.prevY) &&
      Math.abs(roundedY - state.prevY) <= CURSOR_EPSILON
    ) {
      return false;
    }

    state.prevX = roundedX;
    state.prevY = roundedY;

    const valueX = roundedX.toFixed(2);
    const valueY = roundedY.toFixed(2);

    this.applyToElementAndConnects(object, (el) => {
      styleTxn.setVars(el, { "--x": valueX, "--y": valueY });
    });

    return true;
  }

  private calculateOffset(alignment: string, mousePos: number, size: number): number {
    switch (alignment) {
      case "start":
        return mousePos / size;
      case "end":
        return (mousePos - size) / size;
      case "center":
      default:
        return (mousePos - size / 2) / (size / 2);
    }
  }

  override removeObject(id: string): void {
    if (!this.enabled) return super.removeObject(id);
    const obj = this.objectMapOnPage.get(id);
    if (obj) {
      this.centers.detach(obj);
    }
    super.removeObject(id);
  }

  override destroy(): void {
    this.unbindGlobalLifecycleListeners();
    this.hoveredObjects.clear();
    super.destroy();
  }
}
